(() => {
  'use strict';

  const SCRIPT_URL = 'https://payments.maksuturva.fi/tools/partpayment/partPayment.js';
  const BLOCK_SELECTOR = '[data-svea-block]';
  const MANAGER_KEY = 'WorkRelaxSveaPartPayment';

  if (window[MANAGER_KEY]) {
    window[MANAGER_KEY].scan(document);
    return;
  }

  const states = new WeakMap();
  let initializeTimer;
  let unsubscribeVariantChange;

  function getBlocks(root = document) {
    const blocks = [];

    if (root instanceof Element && root.matches(BLOCK_SELECTOR)) {
      blocks.push(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll(BLOCK_SELECTOR).forEach((block) => blocks.push(block));
    }

    return blocks;
  }

  function hasSveaApi() {
    return typeof window.SveaPartPayment?.initializeWidgets === 'function';
  }

  function normalizeSectionId(sectionId) {
    if (!sectionId) return '';
    return sectionId.startsWith('Sticky-') ? sectionId.slice(7) : sectionId;
  }

  function normalizeThresholdPrices(value) {
    try {
      const thresholds = JSON.parse(value || '[]');
      return Array.isArray(thresholds) ? JSON.stringify(thresholds) : '[]';
    } catch (error) {
      return '[]';
    }
  }

  function readConfig(block) {
    const rawPriceCents = block.dataset.priceCents;
    const priceCents = rawPriceCents === '' ? Number.NaN : Number(rawPriceCents);
    const radius = Number(block.dataset.borderRadius);
    const layout = ['compact', 'button', 'mini', 'full'].includes(block.dataset.layout) ? block.dataset.layout : 'button';
    const locale = ['fi', 'sv', 'en'].includes(block.dataset.locale) ? block.dataset.locale : 'fi';

    return {
      sellerId: block.dataset.sellerId?.trim() || '',
      priceCents,
      layout,
      locale,
      includeQuantity: block.dataset.includeQuantity === 'true',
      thresholdPrices: normalizeThresholdPrices(block.dataset.thresholdPrices),
      activeColor: block.dataset.activeColor || '#00325C',
      textColor: block.dataset.textColor || '#222222',
      highlightColor: block.dataset.highlightColor || '#00AECE',
      borderColor: block.dataset.borderColor || '#E5E5E5',
      buttonBackgroundColor: block.dataset.buttonBackgroundColor || '#FFFFFF',
      buttonTextColor: block.dataset.buttonTextColor || '#222222',
      buttonBorderColor: block.dataset.buttonBorderColor || '#D8D8D8',
      borderRadius: Number.isFinite(radius) ? Math.max(0, radius) : 6,
      fallbackText: block.dataset.fallbackText || '',
      valid: block.dataset.configurationValid === 'true' && Boolean(block.dataset.sellerId?.trim()) && Number.isFinite(priceCents),
    };
  }

  function getQuantity(block) {
    const productSection = block.closest('section[data-section-id]');
    const quantityInput = productSection?.querySelector(
      '.main-product__buy-buttons--container input.js-counter-quantity[name="quantity"], .main-product__buy-buttons--container input[name="quantity"]',
    );
    const quantity = Number(quantityInput?.value);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  function formatPrice(priceCents, quantity = 1) {
    if (!Number.isFinite(priceCents)) return '';

    const safeQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    return (Math.round(priceCents * safeQuantity) / 100).toFixed(2);
  }

  function clearWatchdog(state) {
    if (!state?.watchdog) return;
    window.clearTimeout(state.watchdog);
    state.watchdog = null;
  }

  function showFallback(block, state) {
    const host = block.querySelector('[data-svea-widget-host]');
    const fallback = block.querySelector('[data-svea-fallback]');

    if (host) host.hidden = true;
    if (fallback) fallback.hidden = false;
    block.dataset.sveaStatus = 'fallback';
    clearWatchdog(state);
  }

  function hideFallback(block) {
    const host = block.querySelector('[data-svea-widget-host]');
    const fallback = block.querySelector('[data-svea-fallback]');

    if (host) host.hidden = false;
    if (fallback) fallback.hidden = true;
  }

  function hasRenderedWidget(block) {
    const host = block.querySelector('[data-svea-widget-host]');
    return Boolean(host?.querySelector('section, .svea-pp-widget-container, .svea-pp-widget-button, .svea-pp-widget-mini'));
  }

  function refreshBlockStatus(block) {
    const state = states.get(block);
    if (!state || state.libraryFailed || !Number.isFinite(state.variantPriceCents)) {
      showFallback(block, state);
      return;
    }

    if (hasRenderedWidget(block)) {
      hideFallback(block);
      block.dataset.sveaStatus = 'ready';
      clearWatchdog(state);

      // A variant or quantity can change while Svea is still loading its plans.
      if (state.lastDispatchedPrice !== block.dataset.price) {
        updateBlockPrice(block);
      }
    }
  }

  function startWatchdog(block, state) {
    clearWatchdog(state);
    state.watchdog = window.setTimeout(() => {
      if (!block.isConnected || hasRenderedWidget(block)) {
        refreshBlockStatus(block);
        return;
      }

      showFallback(block, state);
    }, 15000);
  }

  function pruneRemovedSveaWidgets(container) {
    if (!Array.isArray(window.SveaPartPayment?.widgets)) return;

    window.SveaPartPayment.widgets = window.SveaPartPayment.widgets.filter((widget) => {
      if (!widget?.parentNode || !widget.isConnected) return false;
      return !container || !container.contains(widget);
    });
  }

  function scheduleSveaInitialization() {
    if (!hasSveaApi()) return;

    window.clearTimeout(initializeTimer);
    initializeTimer = window.setTimeout(() => {
      pruneRemovedSveaWidgets();

      try {
        window.SveaPartPayment.initializeWidgets(() => {
          getBlocks(document).forEach(refreshBlockStatus);
        });
      } catch (error) {
        getBlocks(document).forEach((block) => showFallback(block, states.get(block)));
      }
    }, 0);
  }

  function setWidgetAttributes(script, config, price) {
    script.className = 'svea-pp-widget-part-payment';
    script.src = SCRIPT_URL;
    script.setAttribute('data-sellerid', config.sellerId);
    script.setAttribute('data-price', price);
    script.setAttribute('data-locale', config.locale);
    script.setAttribute('data-threshold-prices', config.thresholdPrices);
    script.setAttribute('data-active-color', config.activeColor);
    script.setAttribute('data-text-color', config.textColor);
    script.setAttribute('data-highlight-color', config.highlightColor);
    script.setAttribute('data-border-color', config.borderColor);
    script.setAttribute('data-border-radius', `${config.borderRadius}px`);
    script.setAttribute('data-button-background-color', config.buttonBackgroundColor);
    script.setAttribute('data-button-text-color', config.buttonTextColor);
    script.setAttribute('data-button-border-color', config.buttonBorderColor);
    script.setAttribute('data-button-border-radius', `${config.borderRadius}px`);
    script.setAttribute('data-fallback-text-fi', config.fallbackText);
    script.setAttribute('data-fallback-text-sv', config.fallbackText);
    script.setAttribute('data-fallback-text-en', config.fallbackText);

    if (config.layout === 'compact' || config.layout === 'button') {
      script.setAttribute('data-layout', 'mini');
    } else if (config.layout === 'mini') {
      script.setAttribute('data-layout', config.layout);
    }
  }

  function handleLibraryFailure(script) {
    if (script) script.dataset.sveaLibraryLoader = 'failed';

    getBlocks(document).forEach((block) => {
      const state = states.get(block);
      if (!state) return;
      state.libraryFailed = true;
      showFallback(block, state);
    });
  }

  function initializeBlock(block) {
    const existingState = states.get(block);

    if (block.dataset.sveaInitialized === 'true' && existingState) {
      if (!existingState.libraryFailed) {
        if (hasSveaApi() && !hasRenderedWidget(block)) scheduleSveaInitialization();
        return;
      }

      clearWatchdog(existingState);
      existingState.observer?.disconnect();
    }

    const config = readConfig(block);
    const state = {
      config,
      variantPriceCents: config.priceCents,
      script: null,
      watchdog: null,
      observer: null,
      lastDispatchedPrice: '',
      libraryFailed: false,
    };
    states.set(block, state);

    if (!config.valid) {
      block.dataset.sveaInitialized = 'true';
      showFallback(block, state);
      return;
    }

    const host = block.querySelector('[data-svea-widget-host]');
    if (!host) {
      block.dataset.sveaInitialized = 'true';
      showFallback(block, state);
      return;
    }

    host.replaceChildren();
    hideFallback(block);

    // The vendor does not consistently call initializeWidgets' callback.
    // Observe its actual output, including a successful response after the watchdog.
    state.observer = new MutationObserver(() => refreshBlockStatus(block));
    state.observer.observe(host, { childList: true, subtree: true });

    const quantity = config.includeQuantity ? getQuantity(block) : 1;
    const price = formatPrice(config.priceCents, quantity);
    const script = document.createElement('script');
    const loaderIsPending = Boolean(document.querySelector('script[data-svea-library-loader="loading"]'));
    const shouldLoadLibrary = !hasSveaApi() && !loaderIsPending;

    if (!shouldLoadLibrary) {
      script.type = 'application/json';
    }

    setWidgetAttributes(script, config, price);
    state.script = script;
    state.lastDispatchedPrice = price;
    block.dataset.price = price;
    block.dataset.sveaInitialized = 'true';
    block.dataset.sveaStatus = 'loading';

    if (shouldLoadLibrary) {
      script.dataset.sveaLibraryLoader = 'loading';
      script.async = false;
      script.defer = true;
      script.addEventListener('load', () => {
        script.dataset.sveaLibraryLoader = 'loaded';
        if (!hasSveaApi()) {
          handleLibraryFailure(script);
          return;
        }

        // Mark the library's first pass as started so DOMContentLoaded cannot race it.
        if (window.SveaPartPayment.initialLoadDone !== true) {
          document.dispatchEvent(new CustomEvent('svea-partpayment-calculator'));
        } else {
          scheduleSveaInitialization();
        }
      }, { once: true });
      script.addEventListener('error', () => handleLibraryFailure(script), { once: true });
    }

    host.appendChild(script);
    startWatchdog(block, state);

    if (hasSveaApi()) scheduleSveaInitialization();
  }

  function scan(root = document) {
    getBlocks(root).forEach(initializeBlock);
  }

  function findBlocksForSection(sectionId) {
    const normalizedSectionId = normalizeSectionId(sectionId);
    return getBlocks(document).filter((block) => block.dataset.sectionId === normalizedSectionId);
  }

  function updateBlockPrice(block, nextPriceCents, quantityOverride) {
    const state = states.get(block);
    if (!state || state.libraryFailed) return;

    if (nextPriceCents !== undefined) {
      const numericPrice = Number(nextPriceCents);
      if (!Number.isFinite(numericPrice)) {
        showFallback(block, state);
        return;
      }
      state.variantPriceCents = numericPrice;
      block.dataset.priceCents = String(numericPrice);
    }

    if (!Number.isFinite(state.variantPriceCents)) {
      showFallback(block, state);
      return;
    }

    let quantity = 1;
    if (state.config.includeQuantity) {
      const requestedQuantity = Number(quantityOverride);
      quantity = Number.isFinite(requestedQuantity) && requestedQuantity > 0 ? requestedQuantity : getQuantity(block);
    }

    const price = formatPrice(state.variantPriceCents, quantity);
    if (!price) {
      showFallback(block, state);
      return;
    }

    block.dataset.price = price;
    state.script?.setAttribute('data-price', price);

    if (!hasSveaApi() || !hasRenderedWidget(block)) return;

    hideFallback(block);
    block.dataset.sveaStatus = 'ready';
    if (state.lastDispatchedPrice === price) return;

    state.lastDispatchedPrice = price;
    document.dispatchEvent(new CustomEvent('svea-partpayment-calculator-update-price', {
      detail: { price },
    }));
  }

  function handleVariantChange(event) {
    const payload = event?.data || event?.detail || {};
    const sectionId = payload.sectionId;
    const variant = payload.variant;
    const blocks = findBlocksForSection(sectionId);

    if (!blocks.length) return;

    if (!variant || !Number.isFinite(Number(variant.price))) {
      blocks.forEach((block) => {
        const state = states.get(block);
        if (state) state.variantPriceCents = Number.NaN;
        showFallback(block, state);
      });
      return;
    }

    blocks.forEach((block) => updateBlockPrice(block, variant.price));
  }

  function handleProductInput(event) {
    if (!(event.target instanceof Element)) return;

    const quantityInput = event.target.matches('input[name="quantity"]')
      && event.target.closest('.main-product__buy-buttons--container')
      ? event.target
      : null;
    if (!quantityInput) return;

    const productSection = quantityInput.closest('section[data-section-id]');
    const sectionId = productSection?.dataset.sectionId;

    findBlocksForSection(sectionId).forEach((block) => {
      const state = states.get(block);
      if (state?.config.includeQuantity) {
        updateBlockPrice(block, undefined, Number(quantityInput.value));
      }
    });
  }

  function handleSectionUnload(event) {
    getBlocks(event.target).forEach((block) => {
      const state = states.get(block);
      clearWatchdog(state);
      state?.observer?.disconnect();
    });
    pruneRemovedSveaWidgets(event.target);
  }

  function subscribeToThemeVariantChanges() {
    if (unsubscribeVariantChange) return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, handleVariantChange);
  }

  const manager = { scan, updateBlockPrice };
  window[MANAGER_KEY] = manager;

  subscribeToThemeVariantChanges();
  document.addEventListener('afterVariantChanged', handleVariantChange);
  document.addEventListener('change', handleProductInput);
  document.addEventListener('input', handleProductInput);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:block:select', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', handleSectionUnload);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }
})();
